import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, MenuItem, Paper, Select, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import DeleteIconButton from '../components/DeleteIconButton';
import config from '../config';
import { normalizeCssColor } from './colorUtils';
import { CREATE, DataAPIContext } from '../api';
import { UPLOAD } from '../api/dataProvider';

const WIDGET_BASE = (config.widgetEmbedBaseUrl || '').replace(/\/$/, '');
// The backend's preview token lasts 4h; refresh well before it lapses.
const PREVIEW_TOKEN_REFRESH_MS = 3 * 60 * 60 * 1000;
// Preview height — tall enough that the opened panel renders at its real
// desktop size. embed.js sizes the panel to viewport − 124px, capped at 750px,
// so an 874px+ viewport hits the cap; 880 shows the widget as most visitors
// see it, and anything taller only adds empty sample page above it.
const WIDGET_PREVIEW_HEIGHT = 880;

// Defaults shown in the preview / placeholders when nothing is configured yet.
// Kept in sync with Tour_Operator.WEB_CHAT_WIDGET_DEFAULTS on the backend.
const DEFAULTS = {
  brand_color: '#0C4A6E',
  brand_tagline: 'Keep your guests happy, on time, and connected.',
  welcome_message: "👋 Hi there! Ask me anything about tours, bookings, or travel.",
  composer_placeholder: 'Ask about your tours or bookings…',
  widget_position: 'bottom-right',
};

// Built-in icon SVGs (mirror of WIDGET_ICONS in widget.html) so the picker can
// show a preview of each option. Order here is the order shown in the dropdown.
const ICON_SVGS = {
  star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  location: '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  map: '<polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>',
  clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  calendar: '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  info: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  phone: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>',
  video: '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>',
  ticket: '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
  globe: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  chat: '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  bus: '<rect x="3" y="4" width="18" height="13" rx="2"/><line x1="3" y1="11" x2="21" y2="11"/><circle cx="7.5" cy="18" r="1.5"/><circle cx="16.5" cy="18" r="1.5"/>',
  hotel: '<path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/>',
  wifi: '<path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/>',
  food: '<path d="M3 2v7c0 1.1.9 2 2 2a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3z"/><path d="M21 15v7"/>',
  camera: '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>',
  sun: '<circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>',
  'cloud-sun': '<path d="M12 2v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="M20 12h2"/><path d="m19.07 4.93-1.41 1.41"/><path d="M15.947 12.65a4 4 0 0 0-5.925-4.128"/><path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z"/>',
  'cloud-sun-rain': '<path d="M12 2v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="M20 12h2"/><path d="m19.07 4.93-1.41 1.41"/><path d="M15.947 12.65a4 4 0 0 0-5.925-4.128"/><path d="M3 20a5 5 0 1 1 8.9-4H13a3 3 0 0 1 2 5.24"/><path d="M11 20v2"/><path d="M7 19v2"/>',
  anchor: '<circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/>',
  card: '<rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>',
  compass: '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>',
  plane: '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>',
};
const ICON_OPTIONS = Object.keys(ICON_SVGS);

// Small visual preview of an icon value: built-in SVG, image URL, or emoji/text.
const IconGlyph = ({ value, size = 18 }) => {
  if (!value) return null;
  const v = String(value).trim();
  if (ICON_SVGS[v]) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: ICON_SVGS[v] }} />
    );
  }
  if (/^(https?:)?\/\//.test(v) || v.startsWith('data:')) {
    return <img src={v} alt="" style={{ width: size, height: size, objectFit: 'contain' }} />;
  }
  return <span style={{ fontSize: size - 1, lineHeight: 1 }}>{v}</span>;
};

// Dropdown wrapper that appends a hint making the free-entry behavior discoverable.
const IconPickerPaper = ({ children, ...rest }) => (
  <Paper {...rest}>
    {children}
    <Box
      onMouseDown={(e) => e.preventDefault()}  /* don't blur/commit when clicking the hint */
      sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', color: 'text.secondary', fontSize: 12 }}
    >
      …or type any emoji, or paste an image URL
    </Box>
  </Paper>
);

// Searchable icon picker: pick a built-in icon (with preview) or type an emoji /
// paste an image URL (freeSolo). Controlled via the input value (the icon string).
const IconField = ({ value, onChange, disabled, label = 'Icon', sx }) => (
  <Autocomplete
    freeSolo
    autoSelect          /* commit on blur, not on every keystroke */
    selectOnFocus
    handleHomeEndKeys
    size="small"
    disabled={disabled}
    options={ICON_OPTIONS}
    value={value || null}
    onChange={(e, v) => onChange(v || '')}
    PaperComponent={IconPickerPaper}
    sx={sx}
    renderOption={(props, option) => (
      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconGlyph value={option} /> {option}
      </Box>
    )}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="filled"
        size="small"
        label={label}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          ...params.InputProps,
          // Show, don't tell: when empty, a faded travel emoji hints that an icon
          // goes here. When a built-in/image icon is set, preview it. When the
          // user has typed an emoji/text, show nothing extra (the input already
          // shows it — echoing it caused the "s s" duplication).
          startAdornment: !value ? (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, pr: 0.5, opacity: 0.3 }}>
              <IconGlyph value="location" />
            </Box>
          ) : (ICON_SVGS[String(value).trim()]
            || /^(https?:)?\/\//.test(value) || String(value).startsWith('data:')) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, pr: 0.5 }}>
              <IconGlyph value={value} />
            </Box>
          ) : null,
        }}
      />
    )}
  />
);

// Live character counter shown as a field's helper text, so the limit is visible
// and it's clear the moment you hit it (turns amber at the max). An optional
// leading hint sits on the left. Rendered with spans only — valid inside the
// helper-text <p>.
const charCountHelper = (value, max, hint) => {
  const len = (value || '').length;
  return (
    <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <span>{hint || ''}</span>
      <Box component="span" sx={{ flexShrink: 0, color: len >= max ? 'warning.main' : 'text.secondary' }}>
        {len}/{max}
      </Box>
    </Box>
  );
};

// A canned answer is only safe when it is the SAME for every visitor. Booking-specific
// phrasing ("your pickup is at…") would be wrong for most askers, so warn rather than
// silently letting an operator publish it. Widget visitors are anonymous pre-sales
// traffic, so anything booking-shaped almost certainly belongs on the AI path.
const BOOKING_SPECIFIC_RE =
  /\b(your (booking|pickup|tour|reservation|guide)|my (booking|pickup|tour|reservation)|meeting point|pick ?up (time|point|location)|confirmation number|booking (ref|number))\b/i;

// A link-button URL must be a real, navigable http(s) URL. Empty is treated
// separately (required), so this only judges the format of a non-empty value.
// NOTE: new URL() alone is too lenient — it normalizes "https:loom.com" (no
// slashes) to host "loom.com" and passes a protocol check, but the widget renders
// the raw string as the href and browsers won't navigate it. So require an explicit
// "http://" / "https://" prefix AND a dotted hostname.
const isValidHttpUrl = (u) => {
  const s = String(u || '').trim();
  if (!/^https?:\/\//i.test(s)) return false;
  try {
    const { hostname } = new URL(s);
    return !!hostname && hostname.includes('.') && !hostname.endsWith('.');
  } catch {
    return false;
  }
};

/**
 * Settings tab for the embeddable web chat widget. Reads/writes
 * config.web_chat_widget.* and config.web_chat_widget_enabled through the shared
 * useGeneralSettings handlers (onSubfieldChange), so it persists via the page's
 * existing Save button. Includes image uploads, a live preview, and the embed
 * snippet operators paste on their site.
 */
export const widgetConfigPath = (widget) => (
  widget && !widget.is_default && widget.config_key
    ? `config.web_chat_widgets.${widget.config_key}`
    : 'config.web_chat_widget'
);

export const widgetConfigFrom = (config, widget) => {
  const c = config || {};
  if (widget && !widget.is_default && widget.config_key) {
    return ((c.web_chat_widgets || {})[widget.config_key]) || {};
  }
  return c.web_chat_widget || {};
};

const WebWidgetSettings = ({
  top, onSubfieldChange, setTopAndDirty, hasWritePerm, hasDeletePerm, savedConfig,
  hideAssistantInstructions, onValidityChange, widget,
}) => {
  const dataAPI = React.useContext(DataAPIContext);
  const { enqueueSnackbar } = useSnackbar();
  // CP-8335: an operator with several branded widgets edits one at a time (the page's
  // widget switcher). The default widget keeps config.web_chat_widget; each other one has
  // its blob under config.web_chat_widgets.<id>, with its own live switch (`enabled`).
  const isOtherWidget = !!(widget && !widget.is_default);
  const cfgPath = widgetConfigPath(widget);
  const cfg = widgetConfigFrom(top.config, widget);
  const operatorEnabled = !!(top.config && top.config.web_chat_widget_enabled);
  const enabled = isOtherWidget ? operatorEnabled && !!cfg.enabled : operatorEnabled;
  const widgetKey = isOtherWidget ? widget.key : '';
  const operatorCode = top.code || '';
  // Sentinel: branding_banner_url === 'none' means "show no banner" (distinct
  // from blank, which falls back to the default TourOptima banner).
  const noBanner = cfg.branding_banner_url === 'none';

  const [uploading, setUploading] = React.useState({ logo: false, banner: false });
  const logoInputRef = React.useRef(null);
  const bannerInputRef = React.useRef(null);
  const previewIframeRef = React.useRef(null);

  // Preview token: lets the preview below send messages (and get AI answers) while the
  // widget is available but not yet switched on, and marks its chats as tests so they
  // stay out of metrics and billing. If it can't be fetched (older backend, no
  // permission) the preview simply behaves as before. Write-only: read-only users get a
  // look-only preview (see the iframe below), and the endpoint requires web_widget.write.
  const [previewToken, setPreviewToken] = React.useState(null);
  React.useEffect(() => {
    if (!operatorCode || !hasWritePerm) return undefined;
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const res = await dataAPI(CREATE, 'web-chat', {
          data: { operator_id: operatorCode },
          urlSuffix: 'preview-token/',
          // A background fetch: a 403 (widget turned off, or a backend that still treats an
          // unset flag as off) only means "no preview", never a sign-out.
          keepSessionOn403: true,
        });
        const token = res && res.statusCode === 200 && res.data && res.data.token;
        if (!cancelled && token) setPreviewToken(token);
      } catch (e) {
        // non-fatal: see above
      }
    };
    fetchToken();
    const timer = setInterval(fetchToken, PREVIEW_TOKEN_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dataAPI, operatorCode, hasWritePerm]);
  // Remember the last real banner URL so toggling "No banner" on and back off
  // restores it, instead of leaving the URL field permanently blank (the
  // checkbox overwrites the field with the 'none' sentinel).
  const lastBannerUrlRef = React.useRef('');
  React.useEffect(() => {
    if (cfg.branding_banner_url && cfg.branding_banner_url !== 'none') {
      lastBannerUrlRef.current = cfg.branding_banner_url;
    }
  }, [cfg.branding_banner_url]);

  // Reset the preview widget's stored session (the widget hides the banner /
  // link buttons / example questions once a conversation has guest messages,
  // and the session persists in localStorage — so after a test message the
  // preview looks "broken" on every reload). embed.js exposes
  // window.TourOptimaChat.reset() on the faux preview page, which is
  // same-origin with the dashboard (srcDoc), so we can call it directly.
  const resetPreviewConversation = () => {
    try {
      const api = previewIframeRef.current
        && previewIframeRef.current.contentWindow
        && previewIframeRef.current.contentWindow.TourOptimaChat;
      if (api && typeof api.reset === 'function') {
        api.reset();
        enqueueSnackbar('Preview conversation reset', { variant: 'success' });
      } else {
        enqueueSnackbar('Preview not ready yet — try again in a moment', { variant: 'warning' });
      }
    } catch (e) {
      enqueueSnackbar('Could not reset the preview conversation', { variant: 'error' });
    }
  };

  const setField = React.useCallback(
    (key, value) => onSubfieldChange(`${cfgPath}.${key}`, value),
    [onSubfieldChange, cfgPath]
  );
  const [accentColorError, setAccentColorError] = React.useState(false);
  const [brandColorError, setBrandColorError] = React.useState(false);

  const handleUpload = async (file, kind) => {
    if (!file) return;
    const isImage = /image\/(png|jpe?g)/i.test(file.type) || /\.(png|jpe?g)$/i.test(file.name || '');
    if (!isImage) {
      enqueueSnackbar('Please select a PNG or JPEG image', { variant: 'error' });
      return;
    }
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const res = await dataAPI(UPLOAD, 'web-chat', { data: { file }, urlSuffix: 'upload-image/' });
      const url = res && res.data && res.data.url;
      if ((res.statusCode === 200 || res.statusCode === 201) && url) {
        setField(kind === 'logo' ? 'brand_logo_url' : 'branding_banner_url', url);
        enqueueSnackbar('Image uploaded', { variant: 'success' });
      } else {
        enqueueSnackbar('Upload failed', { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar('Upload failed', { variant: 'error' });
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  // Sample question/link seeded into the editors when the operator has never
  // configured these, so they show as editable rows the operator can keep, edit,
  // or delete (deleting all + saving shows the empty state). Mirrors the backend
  // defaults so the live widget matches when nothing is saved. An explicit []
  // (operator cleared them) is respected and not re-seeded.
  const SAMPLE_TOPICS = [{ text: 'What are your most popular tours?', icon: 'star' }];
  const SAMPLE_LINKS = [{ text: 'Visit our website', url: 'https://touroptima.com/', icon: 'globe' }];

  // ─── Example topics ({ text, icon, answer }; legacy plain strings normalized) ────
  const rawTopics = cfg.example_topics === undefined
    ? SAMPLE_TOPICS
    : (Array.isArray(cfg.example_topics) ? cfg.example_topics : []);
  // `answer` MUST survive this normalization: it round-trips through save, so dropping it
  // here doesn't just blank the field — the next save persists the stripped array and the
  // backend sync then deletes the exact-match rules. (That happened: answers seeded via
  // config were invisible here and one Save away from being wiped.)
  const topics = rawTopics
    .map((t) => (t && typeof t === 'object'
      ? { text: t.text || '', icon: t.icon || '', answer: t.answer || '' }
      : { text: String(t), icon: '', answer: '' }));
  const setTopics = (next) => setField('example_topics', next);
  const onTopicChange = (idx, field, value) =>
    setTopics(topics.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  const onTopicAdd = () => setTopics([...topics, { text: '', icon: '', answer: '' }]);
  const onTopicDelete = (idx) => setTopics(topics.filter((_, i) => i !== idx));

  // ─── Link buttons (array of { text, url, icon }) ─────────
  const links = cfg.link_buttons === undefined
    ? SAMPLE_LINKS
    : (Array.isArray(cfg.link_buttons) ? cfg.link_buttons : []);
  const setLinks = (next) => setField('link_buttons', next);
  const onLinkChange = (idx, field, value) =>
    setLinks(links.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  const onLinkAdd = () => setLinks([...links, { text: '', url: '', icon: '' }]);
  const onLinkDelete = (idx) => setLinks(links.filter((_, i) => i !== idx));

  // Every configured button must have text and a valid http(s) URL. Report validity
  // up so the page can block Save (and BlockRedirect) on incomplete/invalid buttons.
  const linkButtonsValid = (links || []).every((l) => (l.text && l.text.trim()) && isValidHttpUrl(l.url));
  React.useEffect(() => {
    if (onValidityChange) onValidityChange(linkButtonsValid);
  }, [linkButtonsValid, onValidityChange]);

  // ─── Live preview document ───────────────────────────────
  // Render a faux website page that loads the real embed.js, so the preview
  // shows the corner launcher exactly as a visitor sees it. data-start-open makes
  // it load already-open (no closed→open animation when edits reload the doc).
  const escapeAttr = (s) =>
    String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/'/g, '&#39;');

  const previewDoc = React.useMemo(() => {
    if (!WIDGET_BASE || !operatorCode) return null;
    const attr = (name, val) =>
      (val === undefined || val === null || val === '') ? '' : ` ${name}="${escapeAttr(String(val))}"`;
    // Strip `answer`: the widget renders buttons from text+icon only, and the instant
    // reply must come back through the normal chat pipeline (so it is logged and
    // attributed) rather than being handed to the client.
    const cleanTopics = (topics || [])
      .filter((t) => t && t.text)
      .map(({ answer, ...rest }) => rest);
    const cleanLinks = (links || []).filter((l) => l.text && l.url);
    const dataAttrs =
      attr('data-operator-id', operatorCode) +
      attr('data-widget-id', widgetKey) +
      attr('data-api-base-url', config.apiUrl) +
      attr('data-brand-color', cfg.brand_color) +
      attr('data-accent-color', cfg.accent_color) +
      attr('data-brand-title', cfg.brand_title || top.name) +
      attr('data-brand-tagline', cfg.brand_tagline) +
      // Pass the "none" sentinel when the logo is cleared so the preview shows the
      // DEFAULT icon immediately, instead of the widget falling back to the last-saved
      // logo (which would otherwise persist in the preview until a page refresh).
      attr('data-brand-logo-url', cfg.brand_logo_url || 'none') +
      attr('data-branding-banner-url', cfg.branding_banner_url) +
      attr('data-welcome-message', cfg.welcome_message) +
      attr('data-composer-placeholder', cfg.composer_placeholder) +
      attr('data-widget-position', cfg.widget_position) +
      (cleanTopics.length ? attr('data-example-topics', JSON.stringify(cleanTopics)) : '') +
      (cleanLinks.length ? attr('data-link-buttons', JSON.stringify(cleanLinks)) : '') +
      ' data-start-open="true"' +   // preview opens (and stays open) without the closed→open animation
      // Ephemeral: don't restore/persist the test conversation across reloads, so
      // every edit reloads to the fresh landing view (banner / buttons / questions)
      // instead of reopening the last test chat. Typing in the widget still keeps
      // the conversation going within a single load.
      ' data-ephemeral-session="true"' +
      attr('data-preview-token', previewToken);
    return [
      '<!doctype html><html><head><meta charset="utf-8"><title>Widget preview</title><style>',
      'html,body{margin:0;height:100%}',
      'body{font-family:system-ui,-apple-system,sans-serif;background:#eef1f5;color:#9aa3b2}',
      '.bar{height:40px;background:#fff;border-bottom:1px solid #e3e7ee;display:flex;align-items:center;padding:0 14px;font-size:13px;gap:6px}',
      '.dot{width:10px;height:10px;border-radius:50%}',
      '.page{padding:26px 22px;max-width:680px}',
      'h1{font-size:19px;margin:6px 0 12px;color:#5b6470}',
      '.sk{height:11px;background:#e3e7ee;border-radius:6px;margin:9px 0}',
      '</style></head><body>',
      '<div class="bar"><span class="dot" style="background:#ff5f57"></span><span class="dot" style="background:#febc2e"></span><span class="dot" style="background:#28c840"></span>&nbsp;your-website.com</div>',
      '<div class="page"><h1>Sample page</h1>',
      '<div class="sk" style="width:90%"></div><div class="sk" style="width:74%"></div><div class="sk" style="width:82%"></div><div class="sk" style="width:66%"></div>',
      '</div>',
      `<script src="${WIDGET_BASE}/embed.js"${dataAttrs} async></script>`,
      '</body></html>',
    ].join('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cfg), operatorCode, widgetKey, top.name, previewToken]);

  // Debounce the inline iframe so typing in the form doesn't reload embed.js
  // (and re-fetch config) on every keystroke. The new-tab button uses the
  // immediate doc.
  const [debouncedDoc, setDebouncedDoc] = React.useState(previewDoc);
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedDoc(previewDoc), 600);
    return () => clearTimeout(t);
  }, [previewDoc]);

  // ─── Embed snippet ───────────────────────────────────────
  const snippet =
    `<script\n  src="${WIDGET_BASE}/embed.js"\n  data-operator-id="${operatorCode || 'YOUR_OPERATOR_CODE'}"\n`
    + (widgetKey ? `  data-widget-id="${widgetKey}"\n` : '')
    + '  async\n></script>';

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet).then(
      () => enqueueSnackbar('Embed snippet copied', { variant: 'success' }),
      () => enqueueSnackbar('Could not copy to clipboard', { variant: 'error' })
    );
  };

  // Open the same faux-website preview (launcher + widget) full-window in a new
  // tab — useful when the inline panel is cramped.
  const openPreviewTab = () => {
    if (!previewDoc) return;
    const url = URL.createObjectURL(new Blob([previewDoc], { type: 'text/html' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const disabled = !hasWritePerm;

  // Deleting a SAVED button/question needs web_widget.delete — write alone only
  // edits. Saved rows are the first N indices: new rows only append, and without
  // delete perm the saved prefix can't shrink, so index position identifies them.
  // Rows past the saved count were added this session and stay removable with
  // write alone (a half-filled new row would otherwise block Save with no way to
  // back out). UI-level gating only: the save endpoint sees one config write and
  // can't tell a row deletion from an edit.
  const savedCfg = savedConfig || {};
  const savedLinkCount = Array.isArray(savedCfg.link_buttons) ? savedCfg.link_buttons.length : 0;
  const savedTopicCount = Array.isArray(savedCfg.example_topics) ? savedCfg.example_topics.length : 0;
  const deleteRowDisabled = (idx, savedCount) => disabled || (!hasDeletePerm && idx < savedCount);

  return (
    <Grid container direction="row" p={2} spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" fontSize={20}>Web Chat Widget</Typography>
        <Typography variant="body2" color="textSecondary">
          {isOtherWidget
            ? `The ${widget.display_name || widget.name} widget. Changes here apply to every page running this widget's embed snippet — no code changes needed.`
            : 'Embed an AI-powered chat widget on your website. Changes here apply to every site running your embed snippet — no code changes needed.'}
        </Typography>
      </Grid>

      {/* Configured-but-off is otherwise invisible: the page, the branding and the
          embed snippet all look ready, so an operator can build the whole thing —
          or paste the snippet on their site — without realising visitors get
          nothing. Reaching this page means the widget is available, so the only
          missing step is the switch. */}
      {!enabled && (
        <Grid item xs={12} sx={{ pt: '0 !important' }}>
          <Alert severity="warning" sx={{ borderRadius: '12px' }}>
            <strong>Not live yet.</strong>{' '}
            Visitors won&apos;t see this widget on your website, and it won&apos;t answer
            messages, until it&apos;s switched on. You can already test it in the preview below
            (test chats are left out of your metrics)
            {hasWritePerm
              ? ' — use the Widget enabled toggle below when you\'re ready.'
              : '. Contact TourOptima to activate it.'}
          </Alert>
        </Grid>
      )}

      {/* Settings column */}
      <Grid item xs={12} md={7} container spacing={2} alignContent="flex-start">
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {isOtherWidget ? `Enable the ${widget.display_name || widget.name} widget` : 'Enable web chat widget'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {isOtherWidget
                    ? `When on, the widget appears on every page running this widget's embed snippet${operatorEnabled ? '' : ' (your main widget must be switched on too)'}.`
                    : 'When on, the widget appears on every page running your embed snippet.'}
                </Typography>
              </Box>
              <Switch
                checked={isOtherWidget ? !!cfg.enabled : enabled}
                disabled={disabled}
                onChange={(ev) => (isOtherWidget
                  ? setField('enabled', ev.target.checked)
                  : onSubfieldChange('config.web_chat_widget_enabled', ev.target.checked))}
              />
            </Box>
          </Paper>
        </Grid>

        {/* ── General settings ─────────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>General settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Title"
                  placeholder={top.name || 'TourOptima Chat'} InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 40 }}
                  value={cfg.brand_title || ''} onChange={(e) => setField('brand_title', e.target.value)}
                  helperText={charCountHelper(cfg.brand_title, 40)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Brand color"
                  type="text" placeholder={DEFAULTS.brand_color} InputLabelProps={{ shrink: true }}
                  value={cfg.brand_color || ''}
                  onChange={(e) => { setBrandColorError(false); setField('brand_color', e.target.value); }}
                  // Store one format: "white", "#fff", "rgb(...)" become #rrggbb when the field is left.
                  onBlur={(e) => {
                    const raw = (e.target.value || '').trim();
                    if (!raw) return;
                    const hex = normalizeCssColor(raw);
                    if (hex) {
                      if (hex !== raw) setField('brand_color', hex);
                    } else {
                      setBrandColorError(true);
                    }
                  }}
                  error={brandColorError}
                  helperText={brandColorError ? 'Not a colour. Use a hex code like #003e7e or a name like white.' : undefined}
                  InputProps={{
                    endAdornment: (
                      <input
                        type="color" disabled={disabled}
                        value={normalizeCssColor(cfg.brand_color) || DEFAULTS.brand_color}
                        onChange={(e) => setField('brand_color', e.target.value)}
                        style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer' }}
                      />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Accent color"
                  type="text" placeholder="Same as brand color" InputLabelProps={{ shrink: true }}
                  value={cfg.accent_color || ''}
                  onChange={(e) => { setAccentColorError(false); setField('accent_color', e.target.value); }}
                  onBlur={(e) => {
                    const raw = (e.target.value || '').trim();
                    if (!raw) return;
                    const hex = normalizeCssColor(raw);
                    if (hex) {
                      if (hex !== raw) setField('accent_color', hex);
                    } else {
                      setAccentColorError(true);
                    }
                  }}
                  error={accentColorError}
                  helperText={accentColorError
                    ? 'Not a colour. Use a hex code like #FF9400 or a name like orange.'
                    : 'Link buttons and question icons. Leave blank to use the brand color.'}
                  InputProps={{
                    endAdornment: (
                      <input
                        type="color" disabled={disabled}
                        value={normalizeCssColor(cfg.accent_color) || normalizeCssColor(cfg.brand_color) || DEFAULTS.brand_color}
                        onChange={(e) => setField('accent_color', e.target.value)}
                        style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer' }}
                      />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Introduces itself as"
                  placeholder={top.name || ''} InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 80 }}
                  value={cfg.assistant_name || ''} onChange={(e) => setField('assistant_name', e.target.value)}
                  helperText="The company name the assistant says it works for. Leave blank for your company name."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Header tagline"
                  placeholder={DEFAULTS.brand_tagline} InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 80 }}
                  value={cfg.brand_tagline || ''} onChange={(e) => setField('brand_tagline', e.target.value)}
                  helperText={charCountHelper(cfg.brand_tagline, 80)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Icon URL"
                  InputLabelProps={{ shrink: true }} value={cfg.brand_logo_url || ''}
                  onChange={(e) => setField('brand_logo_url', e.target.value)}
                />
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" hidden
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; handleUpload(f, 'logo'); }} />
                <Button size="small" sx={{ mt: 1 }} disabled={disabled || uploading.logo}
                  onClick={() => logoInputRef.current && logoInputRef.current.click()}>
                  {uploading.logo ? <CircularProgress size={16} /> : 'Upload icon'}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={disabled || noBanner} variant="filled" size="small" fullWidth label="Banner URL"
                  InputLabelProps={{ shrink: true }} value={noBanner ? '' : (cfg.branding_banner_url || '')}
                  onChange={(e) => setField('branding_banner_url', e.target.value)}
                />
                <input ref={bannerInputRef} type="file" accept="image/png,image/jpeg" hidden
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; handleUpload(f, 'banner'); }} />
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Button size="small" disabled={disabled || noBanner || uploading.banner}
                    onClick={() => bannerInputRef.current && bannerInputRef.current.click()}>
                    {uploading.banner ? <CircularProgress size={16} /> : 'Upload banner'}
                  </Button>
                  <FormControlLabel
                    control={<Checkbox size="small" />}
                    label="No banner"
                    checked={noBanner}
                    disabled={disabled}
                    onChange={(e) => setField('branding_banner_url', e.target.checked ? 'none' : lastBannerUrlRef.current)}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Buttons</Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  Prominent buttons at the top of the widget (e.g. "Buy tickets"). Pick an
                  icon from the list, or type an emoji or paste an image URL. Up to 3 buttons.
                </Typography>
                {links.map((l, idx) => {
                  const textEmpty = !(l.text && l.text.trim());
                  const urlEmpty = !(l.url && l.url.trim());
                  const urlInvalid = !urlEmpty && !isValidHttpUrl(l.url);
                  return (
                  <Box key={idx} display="flex" alignItems="flex-start" gap={1} mt={1}>
                    <TextField
                      required
                      disabled={disabled} variant="filled" size="small" label="Text" sx={{ flex: 1 }}
                      InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 30 }} value={l.text || ''}
                      onChange={(e) => onLinkChange(idx, 'text', e.target.value)}
                      error={textEmpty}
                      helperText={textEmpty ? 'Button text is required' : charCountHelper(l.text, 30)}
                    />
                    <TextField
                      required
                      disabled={disabled} variant="filled" size="small" label="URL" sx={{ flex: 2 }}
                      InputLabelProps={{ shrink: true }} value={l.url || ''}
                      onChange={(e) => onLinkChange(idx, 'url', e.target.value)}
                      error={urlEmpty || urlInvalid}
                      helperText={urlEmpty ? 'URL is required' : (urlInvalid ? 'Enter a valid URL starting with http:// or https://' : '')}
                    />
                    <IconField
                      disabled={disabled} sx={{ width: 170 }}
                      value={l.icon || ''}
                      onChange={(v) => onLinkChange(idx, 'icon', v)}
                    />
                    <DeleteIconButton
                      title="Delete button"
                      aria-label="delete link"
                      size="medium"
                      fontSize="medium"
                      disabled={deleteRowDisabled(idx, savedLinkCount)}
                      onClick={() => onLinkDelete(idx)}
                    />
                  </Box>
                  );
                })}
                {/* Wrap in a span so the tooltip still fires while the button is
                    disabled (disabled elements don't emit hover events). Empty
                    title below the cap = no tooltip. */}
                <Tooltip title={links.length >= 3 ? 'Maximum of 3 buttons reached.' : ''}>
                  <span>
                    <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
                      disabled={disabled || links.length >= 3} onClick={onLinkAdd}>
                      Add button
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ── AI and messages ──────────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>AI and messages</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth multiline minRows={2}
                  label="Welcome message" placeholder={DEFAULTS.welcome_message} InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 300 }}
                  value={cfg.welcome_message || ''} onChange={(e) => setField('welcome_message', e.target.value)}
                  helperText={charCountHelper(cfg.welcome_message, 300, "The assistant's opening line, shown before the visitor types.")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth label="Composer placeholder"
                  placeholder={DEFAULTS.composer_placeholder} InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 45 }}
                  value={cfg.composer_placeholder || ''} onChange={(e) => setField('composer_placeholder', e.target.value)}
                  helperText={charCountHelper(cfg.composer_placeholder, 45, "Hint text shown inside the empty message box, before the visitor types.")}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Example questions</Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  Shown as quick-reply buttons before the visitor's first message. Pick an
                  icon from the list, or type an emoji or paste an image URL. Add an instant
                  answer to reply the moment the button is tapped, with no AI delay.
                </Typography>
                {topics.map((t, idx) => {
                  const answerLooksBookingSpecific = BOOKING_SPECIFIC_RE.test(t.answer || '');
                  return (
                  <Box
                    key={idx} mt={1.5} p={1.5}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <TextField
                        disabled={disabled} variant="filled" size="small" sx={{ flex: 1 }} label="Question"
                        InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 100 }} value={t.text}
                        onChange={(e) => onTopicChange(idx, 'text', e.target.value)}
                        placeholder="e.g. What time does the tour start?"
                        helperText={charCountHelper(t.text, 100)}
                      />
                      <IconField
                        disabled={disabled} sx={{ width: 180 }}
                        value={t.icon}
                        onChange={(v) => onTopicChange(idx, 'icon', v)}
                      />
                      <DeleteIconButton
                        title="Delete question"
                        aria-label="delete topic"
                        size="medium"
                        fontSize="medium"
                        disabled={deleteRowDisabled(idx, savedTopicCount)}
                        onClick={() => onTopicDelete(idx)}
                      />
                    </Box>
                    {/* AI Quick Response (shown to operators under that name, as on AI Metrics).
                        Filled -> the backend keeps an exact-match rule in
                        step with this button (the question text IS the match phrase), so a
                        tap replies immediately without the AI. Blank -> the button falls
                        through to the assistant exactly as before. */}
                    <TextField
                      disabled={disabled} variant="filled" size="small" fullWidth multiline minRows={2}
                      sx={{ mt: 1 }} label="AI Quick Response (optional)" InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 1000 }}
                      value={t.answer || ''}
                      onChange={(e) => onTopicChange(idx, 'answer', e.target.value)}
                      placeholder="Leave blank to let the assistant determine the best response."
                      error={answerLooksBookingSpecific}
                      helperText={
                        answerLooksBookingSpecific
                          ? "This may be specific to one booking. Website visitors are usually anonymous, so the same response would go to everyone asking this question — if that isn't what you want, leave it blank and let the assistant determine the best response."
                          : (t.answer
                            ? charCountHelper(t.answer, 1000, 'Sent instantly, word for word, whenever someone taps this question.')
                            : charCountHelper(t.answer, 1000, 'Blank means the assistant determines the best response for this one.'))
                      }
                    />
                  </Box>
                  );
                })}
                <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} disabled={disabled} onClick={onTopicAdd}>
                  Add question
                </Button>
              </Grid>
              {!hideAssistantInstructions && (
              <Grid item xs={12}>
                {/* AI persona for the widget. Top-level config key (not in the widget
                    blob), so write it via onSubfieldChange directly. Blank = the
                    default public sales assistant; shares the same knowledge base.
                    On the tabbed Web Widget page this field is hidden here and shown
                    on its own "Assistant" tab instead. */}
                <TextField
                  disabled={disabled} variant="filled" size="small" fullWidth multiline minRows={3}
                  label="Assistant instructions (optional)" InputLabelProps={{ shrink: true }}
                  placeholder="Leave blank to use the default public sales assistant. The widget uses your existing knowledge base."
                  value={(top.config && top.config.chatbot_prompt_web_widget) || ''}
                  onChange={(e) => onSubfieldChange('config.chatbot_prompt_web_widget', e.target.value)}
                  helperText="Overrides the support-chat AI prompt for website visitors only."
                />
              </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* ── Install ──────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Install</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl variant="filled" size="small" fullWidth disabled={disabled}>
                  <InputLabel shrink>Launcher position</InputLabel>
                  <Select
                    value={cfg.widget_position || DEFAULTS.widget_position}
                    onChange={(e) => setField('widget_position', e.target.value)}
                  >
                    <MenuItem value="bottom-right">Bottom right</MenuItem>
                    <MenuItem value="bottom-left">Bottom left</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Embed snippet</Typography>
                <Typography variant="caption" color="textSecondary">
                  Paste this once before the closing &lt;/body&gt; tag on your website.
                </Typography>
                <Box
                  sx={{
                    mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'grey.100', fontFamily: 'monospace',
                    fontSize: 12, whiteSpace: 'pre-wrap', position: 'relative',
                  }}
                >
                  {snippet}
                  <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4 }} onClick={copySnippet} aria-label="copy snippet">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
                {!operatorCode && (
                  <Typography variant="caption" color="error">
                    Save the operator code first to generate a usable snippet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Preview column — sticky so it stays visible while the form scrolls */}
      <Grid item xs={12} md={5} sx={{ position: { md: 'sticky' }, top: { md: 16 }, alignSelf: { md: 'flex-start' } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2">Live preview</Typography>
          {previewDoc && (
            <Box display="flex" gap={1}>
              <Button
                size="small"
                disabled={disabled}
                startIcon={<ReplayIcon fontSize="small" />}
                onClick={resetPreviewConversation}
              >
                Reset conversation
              </Button>
              <Button
                size="small"
                disabled={disabled}
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={openPreviewTab}
              >
                Open in new tab
              </Button>
            </Box>
          )}
        </Box>
        {debouncedDoc ? (
          <Box
            sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 2,
              overflow: 'hidden', height: WIDGET_PREVIEW_HEIGHT, bgcolor: 'grey.100',
            }}
          >
            {/* Faux website page running the real embed.js — shows the corner
                launcher and the widget exactly as a visitor sees them. Because it
                talks to the live API, sending a test message creates a real
                web_widget conversation in the operator's chat list — so testing is
                a write-level action: read-only users get a look-but-don't-type
                preview (pointer events off, new-tab/reset disabled above). The
                widget endpoints themselves are public (site visitors are
                anonymous), so this is dashboard courtesy, not a security boundary. */}
            <iframe
              ref={previewIframeRef}
              key={`${operatorCode || 'preview'}:${widgetKey}`}
              title="Web chat widget preview"
              srcDoc={debouncedDoc}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              style={{ width: '100%', height: '100%', border: 'none', pointerEvents: disabled ? 'none' : 'auto' }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Preview unavailable. Ensure the operator code is saved and the widget host is configured.
          </Typography>
        )}
        {disabled && (
          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
            You have read-only access, so the preview is view-only — sending test
            messages is disabled.
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
          Shows the launcher and widget as a visitor sees them, and reflects unsaved
          edits. If the preview is blank, your widget host is sending an
          <code> X-Frame-Options </code> header that blocks embedding — use
          “Open in new tab”, and serve the widget from a frameable host for production.
        </Typography>
      </Grid>
    </Grid>
  );
};

WebWidgetSettings.propTypes = {
  top: PropTypes.object.isRequired,
  onSubfieldChange: PropTypes.func.isRequired,
  setTopAndDirty: PropTypes.func,
  hasWritePerm: PropTypes.bool,
  hasDeletePerm: PropTypes.bool,
  savedConfig: PropTypes.object,
  hideAssistantInstructions: PropTypes.bool,
  onValidityChange: PropTypes.func,
};

IconGlyph.propTypes = {
  value: PropTypes.string,
  size: PropTypes.number,
};

IconPickerPaper.propTypes = {
  children: PropTypes.node,
};

IconField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  sx: PropTypes.object,
};

export default WebWidgetSettings;
